import { useUser } from '../../user-management/hooks/useUserWithRole';
import { useQuestion } from '../../question/hooks/useQuestions';
import { useSurvey } from '../../survey/hooks/useSurveys';
import { useTenant } from '../../manage/hooks/useTenants';
import { useSubject } from '../../domain/hooks/useSubjects';
import { useDomainInfo } from '../../domain/hooks/useSubjects';
import { useSurveyInstance } from '../../survey-instance/hooks/useSurveyInstances';

/**
 * Hook to get display name for a user ID
 */
export const useUserDisplayName = (userId: string | undefined) => {
  const { data: user, isLoading, isError } = useUser(userId || '');

  const displayName = user?.name || user?.email || 'Unknown User';
  const truncatedId = userId ? `${userId.substring(0, 8)}...` : 'Unknown User';

  return {
    displayName: isError || !user ? truncatedId : displayName,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display text for a question ID
 * Note: This requires surveyId which we don't have in audit trail context
 * For now, we'll return truncated ID until we have a question-by-id-only endpoint
 */
export const useQuestionDisplayText = (questionId: string | undefined, surveyId?: string) => {
  const { data: question, isLoading, isError } = useQuestion(surveyId || '', questionId || '');

  const getDisplayText = (textObj: any): string => {
    if (typeof textObj === 'string') return textObj;
    if (typeof textObj === 'object' && textObj !== null) {
      return (
        textObj.en || textObj.no || textObj.de || Object.values(textObj)[0] || 'Untitled Question'
      );
    }
    return 'Untitled Question';
  };

  // Get question title from the API response
  const questionData = question?.data;
  let displayText: string | undefined;

  if (questionData?.title) {
    displayText = getDisplayText(questionData.title);
  }

  const truncatedId = questionId ? `Question ${questionId.substring(0, 8)}...` : 'Unknown Question';

  // If we got "Text Question" or similar generic type names, fall back to truncated ID
  if (
    displayText &&
    (displayText.toLowerCase().includes('text question') ||
      displayText.toLowerCase().includes('multiple choice') ||
      displayText.toLowerCase().includes('single choice') ||
      displayText.toLowerCase().includes('yes/no') ||
      displayText.toLowerCase() === 'untitled question')
  ) {
    displayText = truncatedId;
  }

  return {
    displayText: isError || !displayText ? truncatedId : displayText,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display title for a survey ID
 */
export const useSurveyDisplayTitle = (surveyId: string | undefined) => {
  const { data: survey, isLoading, isError } = useSurvey(surveyId || '');

  // Handle multilingual titles
  const getDisplayTitle = (titleObj: any): string => {
    if (typeof titleObj === 'string') return titleObj;
    if (typeof titleObj === 'object' && titleObj !== null) {
      return (
        titleObj.en || titleObj.no || titleObj.de || Object.values(titleObj)[0] || 'Untitled Survey'
      );
    }
    return 'Untitled Survey';
  };

  const surveyTitle = survey?.data?.title ? getDisplayTitle(survey.data.title) : undefined;
  const truncatedId = surveyId ? `Survey ${surveyId.substring(0, 8)}...` : 'Unknown Survey';

  return {
    displayTitle: isError || !surveyTitle ? truncatedId : surveyTitle,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display name for a tenant ID
 */
export const useTenantDisplayName = (tenantId: string | undefined) => {
  const { data: tenant, isLoading, isError } = useTenant(tenantId || '');

  const displayName = tenant?.name || 'Unknown University';
  const truncatedId = tenantId ? `University ${tenantId.substring(0, 8)}...` : 'Unknown University';

  return {
    displayName: isError || !tenant ? truncatedId : displayName,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display name for a subject ID
 * Requires tenantId for tRPC key lookup
 */
export const useSubjectDisplayName = (
  tenantId: string | undefined,
  subjectId: string | undefined
) => {
  const { data: subject, isLoading, isError } = useSubject(tenantId || '', subjectId || '');

  const displayName = subject?.name || 'Unknown Subject';
  const truncatedId = subjectId ? `Subject ${subjectId.substring(0, 8)}...` : 'Unknown Subject';

  return {
    displayName: isError || !subject ? truncatedId : displayName,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display name for a domain ID
 * Requires tenantId for tRPC key lookup
 */
export const useDomainDisplayName = (
  tenantId: string | undefined,
  domainId: string | undefined
) => {
  const { data: domain, isLoading, isError } = useDomainInfo(tenantId || '', domainId || '');

  const displayName = domain?.name || 'Unknown Institute';
  const truncatedId = domainId ? `Institute ${domainId.substring(0, 8)}...` : 'Unknown Institute';

  return {
    displayName: isError || !domain ? truncatedId : displayName,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display title for a survey instance ID
 */
export const useSurveyInstanceDisplayTitle = (
  surveyId: string | undefined,
  instanceId: string | undefined
) => {
  const { isLoading, isError } = useSurveyInstance(surveyId || '', instanceId || '');

  const { displayTitle: surveyTitle } = useSurveyDisplayTitle(surveyId);

  // Use survey title since instances don't typically have separate titles
  const instanceTitle = surveyTitle;
  const truncatedId = instanceId ? `Instance ${instanceId.substring(0, 8)}...` : 'Unknown Instance';

  return {
    displayTitle: isError || !instanceTitle ? truncatedId : instanceTitle,
    isLoading,
    isError,
  };
};

/**
 * Hook to get display text for a response ID
 * Since response IDs are UUIDs and don't have meaningful display names,
 * we'll just return a truncated version
 */
export const useResponseDisplayText = (responseId: string | undefined) => {
  const truncatedId = responseId ? `Response ${responseId.substring(0, 8)}...` : 'Unknown Response';

  return {
    displayText: truncatedId,
    isLoading: false,
    isError: false,
  };
};
